import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useInstructorPermissions } from '../hooks/useInstructorPermissions';

const InstructorDashboard = () => {
  const { currentUser, userRole } = useAuth();
  const { permissions, loading } = useInstructorPermissions(currentUser?.uid);

  // Check if user has permission to access instructor dashboard
  // Allow Instructors, Admins, and Owners
  if (!currentUser || !['Instructor', 'Admin', 'owner'].includes(userRole)) {
    return (
      <div className="min-h-screen bg-bg text-text flex flex-col items-center justify-center py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-red-600">Access Denied</h1>
          <p className="text-lg">You don't have permission to access this page.</p>
          <p className="text-sm text-gray-600 mt-2">Only Instructors, Admins, and Owners can access the Instructor Dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-bg text-text flex items-center justify-center">
        <div className="text-lg">Loading your dashboard...</div>
      </div>
    );
  }

  // For Admins and Owners, give full access without checking permissions
  const isAdminOrOwner = ['Admin', 'owner'].includes(userRole);
  
  const hasSkillSheetsAccess = isAdminOrOwner || (permissions?.skillSheets && permissions.skillSheets !== 'none') || 
                               (permissions?.specialAccess && permissions.specialAccess.includes('skillSheets'));
  const hasScenarioBuilderAccess = isAdminOrOwner || (permissions?.scenarioBuilder && permissions.scenarioBuilder !== 'none') ||
                                   (permissions?.specialAccess && permissions.specialAccess.includes('scenarioBuilder'));
  
  // Check for special assignments based on specialAccess array
  const specialAccessModules = permissions?.specialAccess || [];
  const hasSpecialAssignments = !isAdminOrOwner && specialAccessModules.length > 0;

  // Debug logging for Maria
  if (currentUser?.email === 'msepulveda8@ccc.edu') {
    console.log('🔍 MARIA DEBUG INFO:');
    console.log('- User ID:', currentUser?.uid);
    console.log('- User Role:', userRole);
    console.log('- Raw Permissions:', permissions);
    console.log('- Special Access Modules:', specialAccessModules);
    console.log('- Has Skill Sheets Access:', hasSkillSheetsAccess);
    console.log('- Has Scenario Builder Access:', hasScenarioBuilderAccess);
    console.log('- Has Special Assignments:', hasSpecialAssignments);
  }

  const getAccessLevelText = (level) => {
    switch (level) {
      case 'general': return 'General Access';
      case 'special': return 'Special Assignment';
      default: return '';
    }
  };

  const getAccessLevelColor = (level) => {
    switch (level) {
      case 'general': return 'bg-blue-100 text-blue-800';
      case 'special': return 'bg-red-100 text-red-800';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-bg text-text flex flex-col items-center py-8">
      <h1 className="text-2xl font-bold mb-4 text-primary">
        Instructor Dashboard
      </h1>
      <p className="text-lg mb-8 text-center max-w-2xl">
        {isAdminOrOwner 
          ? `Welcome, ${userRole}! You have full access to all instructor modules and features.`
          : 'Welcome, Instructor! Here are the modules you have access to based on your current assignments.'
        }
      </p>
      
      {/* Special Assignments Alert */}
      {hasSpecialAssignments && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 max-w-2xl w-full">
          <h3 className="font-semibold text-red-800 mb-2">🔥 Active Special Assignments (On the Clock)</h3>
          <div className="space-y-1">
            {specialAccessModules.map(moduleId => (
              <div key={moduleId} className="text-red-700">
                • {moduleId === 'skillSheets' ? 'Skill Sheets' : 
                   moduleId === 'scenarioBuilder' ? 'Scenario Builder' : 
                   moduleId.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} - Special Assignment Access
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="flex flex-col gap-4 items-center max-w-md w-full">
        {/* Skill Sheets Module */}
        {hasSkillSheetsAccess && (
          <div className="w-full">
            <Link
              to="/instructor/skill-sheets"
              className="block bg-primary text-white font-semibold px-8 py-4 rounded-lg shadow-lg hover:bg-secondary hover:text-primary transition-all duration-200 text-lg text-center relative"
            >
              <div className="flex items-center justify-center gap-2">
                <span>📋 Skill Sheets</span>
              </div>
              <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs ${
                isAdminOrOwner 
                  ? 'bg-green-100 text-green-800' 
                  : specialAccessModules.includes('skillSheets')
                    ? 'bg-red-100 text-red-800'
                    : getAccessLevelColor(permissions.skillSheets)
              }`}>
                {isAdminOrOwner ? 'Full Access' : 
                 specialAccessModules.includes('skillSheets') ? 'Special Assignment' :
                 getAccessLevelText(permissions.skillSheets)}
              </div>
            </Link>
          </div>
        )}

        {/* Scenario Builder Module */}
        {hasScenarioBuilderAccess && (
          <div className="w-full">
            <Link
              to="/scenario-builder"
              className="block bg-purple-500 text-white font-semibold px-8 py-4 rounded-lg shadow-lg hover:bg-purple-700 transition-all duration-200 text-lg text-center relative"
            >
              <div className="flex items-center justify-center gap-2">
                <span>🎭 Scenario Builder</span>
              </div>
              <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs ${
                isAdminOrOwner 
                  ? 'bg-green-100 text-green-800' 
                  : specialAccessModules.includes('scenarioBuilder')
                    ? 'bg-red-100 text-red-800'
                    : getAccessLevelColor(permissions.scenarioBuilder)
              }`}>
                {isAdminOrOwner ? 'Full Access' : 
                 specialAccessModules.includes('scenarioBuilder') ? 'Special Assignment' :
                 getAccessLevelText(permissions.scenarioBuilder)}
              </div>
            </Link>
          </div>
        )}

        {/* NREMT Assessment Builder Module */}
        {hasSkillSheetsAccess && (
          <div className="w-full">
            <Link
              to="/instructor/nremt-builder"
              className="block bg-blue-600 text-white font-semibold px-8 py-4 rounded-lg shadow-lg hover:bg-blue-800 transition-all duration-200 text-lg text-center relative"
            >
              <div className="flex items-center justify-center gap-2">
                <span>🏥 NREMT Assessment Builder 2.0</span>
              </div>
              <div className="text-xs opacity-75 mt-1">Create NREMT-style evaluation forms</div>
              <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs ${
                isAdminOrOwner 
                  ? 'bg-green-100 text-green-800' 
                  : specialAccessModules.includes('skillSheets')
                    ? 'bg-red-100 text-red-800'
                    : getAccessLevelColor(permissions.skillSheets)
              }`}>
                {isAdminOrOwner ? 'Full Access' : 
                 specialAccessModules.includes('skillSheets') ? 'Special Assignment' :
                 getAccessLevelText(permissions.skillSheets)}
              </div>
            </Link>
          </div>
        )}

        {/* No Access Message */}
        {!hasSkillSheetsAccess && !hasScenarioBuilderAccess && (
          <div className="text-center py-8">
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Module Access</h3>
              <p className="text-gray-600">
                You don't currently have access to any modules. Please contact your administrator to request access.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Access Legend */}
      <div className="mt-8 bg-white border border-gray-200 rounded-lg p-4 max-w-2xl w-full">
        <h3 className="font-semibold text-gray-800 mb-2">Access Levels:</h3>
        <div className="space-y-1 text-sm">
          {isAdminOrOwner && (
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800">Full Access</span>
              <span className="text-gray-600">Complete administrative access to all features</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">General Access</span>
            <span className="text-gray-600">View others' sheets (read-only) + Create/edit your own</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800">Special Assignment</span>
            <span className="text-gray-600">Full access to all sheets (tracked as "on the clock")</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructorDashboard;
